// src/pages/CaseHistoryPage/CaseHistoryPage.js
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import useCaseHistoryEnabled from '../../hooks/useCaseHistoryEnabled';
import SectionRenderer from './SectionRenderer';
import { getCase } from '../IntakeHub/caseHistoryApi';
import { getCompletion, getFormSchema, getSections, saveSection, setSectionNA, submitCase } from './formApi';
import { saveDraft, deleteDraft, countDrafts } from './caseHistoryDrafts';
import { syncDrafts } from './draftSync';
import './CaseHistoryPage.css';

const AUTOSAVE_MS = 20000;

export default function CaseHistoryPage() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { enabled, loading: flagLoading } = useCaseHistoryEnabled();

    const [schema, setSchema] = useState(null);
    const [caseInfo, setCaseInfo] = useState(null);
    const [completion, setCompletion] = useState(null);
    const [answersBySection, setAnswersBySection] = useState({});
    const [editableBySection, setEditableBySection] = useState({});
    const [current, setCurrent] = useState(null);
    const [lang, setLang] = useState('both');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [savedAt, setSavedAt] = useState(null);
    const [pendingDrafts, setPendingDrafts] = useState(0);
    const [submitting, setSubmitting] = useState(false);

    const dirty = useRef(false);
    const currentRef = useRef(null);
    const answersRef = useRef({});
    useEffect(() => { currentRef.current = current; }, [current]);
    useEffect(() => { answersRef.current = answersBySection; }, [answersBySection]);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([getFormSchema(), getSections(caseId), getCompletion(caseId), getCase(caseId)])
            .then(([schemaRes, sectionsRes, completionRes, caseRes]) => {
                setSchema(schemaRes.data);
                setCaseInfo(caseRes.data);
                setCompletion(completionRes.data);
                const answers = {}, editable = {};
                sectionsRes.data.forEach((s) => {
                    answers[s.section_key] = s.answers || {};
                    editable[s.section_key] = s.editable;
                });
                setAnswersBySection(answers);
                setEditableBySection(editable);
                // Resume where the work is, not at section 1 every time.
                setCurrent((c) => c || completionRes.data.first_incomplete || '1');
                setError('');
            })
            .catch((err) => setError(
                err.response?.status === 404 ? 'This case could not be found.'
                    : 'Could not load the form. Check your connection and try again.'
            ))
            .finally(() => setLoading(false));
    }, [caseId]);

    useEffect(() => { if (enabled) load(); }, [enabled, load]);

    const refreshCompletion = useCallback(() => {
        getCompletion(caseId).then((r) => setCompletion(r.data)).catch(() => {});
    }, [caseId]);

    /** Save the section being edited. Called on blur, on a timer, and when leaving a section. */
    const flush = useCallback(async (sectionKey = currentRef.current) => {
        if (!sectionKey || !dirty.current) return;
        const answers = answersRef.current[sectionKey] || {};
        dirty.current = false;
        try {
            await saveSection(caseId, sectionKey, answers);
            await deleteDraft(caseId, sectionKey);
            setSavedAt(new Date());
            setPendingDrafts(await countDrafts(caseId));
            setError('');
            refreshCompletion();
        } catch (err) {
            if (err.response && err.response.status === 400) {
                const detail = err.response.data?.detail;
                setError(typeof detail === 'string' ? detail : 'That answer was not accepted.');
                return;
            }
            // Network, or the server is down: keep it on the device rather than
            // losing 25 minutes of interview.
            await saveDraft(caseId, sectionKey, answers);
            setPendingDrafts(await countDrafts(caseId));
            setSavedAt(new Date());
        }
    }, [caseId, refreshCompletion]);

    // Autosave on a timer while there is unsaved work.
    useEffect(() => {
        const id = setInterval(() => { flush(); }, AUTOSAVE_MS);
        return () => clearInterval(id);
    }, [flush]);

    // Push anything held on the device as soon as the network returns.
    useEffect(() => {
        const onOnline = () => syncDrafts(caseId).then(async (sent) => {
            if (sent) { setPendingDrafts(await countDrafts(caseId)); refreshCompletion(); }
        });
        window.addEventListener('online', onOnline);
        countDrafts(caseId).then(setPendingDrafts).catch(() => {});
        return () => window.removeEventListener('online', onOnline);
    }, [caseId, refreshCompletion]);

    if (flagLoading || loading) return <div className="ch-centre">Loading…</div>;
    if (!enabled) return <div className="ch-centre">Case History is not switched on for this installation yet.</div>;
    if (!schema || !completion) {
        return (
            <div className="ch-centre">
                <div className="ch-note red">{error || 'Could not load the form.'}</div>
                <button className="btn btn-secondary" onClick={() => navigate('/intake')}>Back to Intake Hub</button>
            </div>
        );
    }

    const section = schema.sections.find((s) => s.key === current) || schema.sections[0];
    const status = completion.sections.find((s) => s.key === section.key) || {};
    const editable = editableBySection[section.key] !== false && section.owner === 'intake';
    const answers = answersBySection[section.key] || {};

    const onAnswer = (questionKey, value) => {
        dirty.current = true;
        const next = {
            ...answersRef.current,
            [section.key]: { ...(answersRef.current[section.key] || {}), [questionKey]: value },
        };
        // Update the ref in the same tick: a choice button answers and saves in
        // one handler, and the post-render effect would be too late — the save
        // would carry the previous answer.
        answersRef.current = next;
        setAnswersBySection(next);
    };

    const goTo = async (key) => { await flush(); setCurrent(key); };

    const markNA = async () => {
        const reason = window.prompt('Why does this section not apply?');
        if (!reason || !reason.trim()) return;
        try {
            await setSectionNA(caseId, section.key, reason.trim());
            refreshCompletion();
        } catch {
            setError('Could not mark the section as not applicable.');
        }
    };

    const doSubmit = async () => {
        setSubmitting(true);
        await flush();
        try {
            await submitCase(caseId);
            navigate('/intake');
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(typeof detail === 'string' ? detail
                : detail?.message || 'Could not submit. Some required answers are still missing.');
            refreshCompletion();
        } finally {
            setSubmitting(false);
        }
    };

    const t = (text) => (lang === 'en' ? text.en : text.hi);
    const tBoth = (text) => (lang === 'both' ? `${text.hi} · ${text.en}` : t(text));
    // "§" is a legal citation mark. Nobody filling this form reads it as "section",
    // and in Hindi it means nothing at all — so the word is spelled out.
    const sectionNo = (key) => (lang === 'en' ? `Section ${key}` : `भाग ${key}`);

    return (
        <div className="ch-page">
            <header className="ch-header">
                <div>
                    <h1>{caseInfo?.child_name} · <span className="ch-sub">Episode {caseInfo?.episode_number}</span></h1>
                    <div className="ch-sub">
                        {completion.status} · {schema.version}
                        {status.carried_from_case_id ? ' · carried over from an earlier episode' : ''}
                    </div>
                </div>
                <div className="ch-header-right">
                    <div className="ch-lang">
                        {['hi', 'en', 'both'].map((l) => (
                            <button key={l} className={lang === l ? 'on' : ''} onClick={() => setLang(l)}>
                                {l === 'hi' ? 'हिन्दी' : l === 'en' ? 'English' : 'Both'}
                            </button>
                        ))}
                    </div>
                    <button className="btn btn-secondary" onClick={async () => { await flush(); navigate('/intake'); }}>
                        Back
                    </button>
                    {completion.can_submit ? (
                        <button className="btn btn-primary" onClick={doSubmit} disabled={submitting}>
                            {submitting ? 'Submitting…' : 'Submit to therapist'}
                        </button>
                    ) : (
                        <button className="btn btn-primary" disabled title="Sections 1, 2 and 4 must be complete">
                            Submit
                        </button>
                    )}
                </div>
            </header>

            {error && <div className="ch-note red ch-banner">{error}</div>}

            <div className="ch-body">
                <nav className="ch-rail" aria-label="Sections">
                    {completion.sections.map((s) => (
                        <button
                            key={s.key}
                            className={`ch-rail-item ${s.key === section.key ? 'current' : ''} ${s.complete ? 'done' : ''} ${s.owner !== 'intake' ? 'locked' : ''}`}
                            onClick={() => goTo(s.key)}
                        >
                            <span className="ch-rail-no">{s.key}</span>
                            <span className="ch-rail-title">{t(s.title)}</span>
                            <span className="ch-rail-count">
                                {s.owner === 'therapist' ? '🔒'
                                    : s.owner === 'derived' ? 'auto'
                                        : s.is_na ? 'N/A'
                                            : `${s.answered}/${s.required}`}
                            </span>
                        </button>
                    ))}
                </nav>

                <section className="ch-canvas">
                    <div className="ch-canvas-head">
                        <h2>{sectionNo(section.key)} · {tBoth(section.title)}</h2>
                        {editable && !status.is_na && (
                            <button className="btn btn-secondary" onClick={markNA}>Not applicable</button>
                        )}
                    </div>

                    {!editable && section.owner === 'intake' && (
                        <div className="ch-note amber">
                            Read-only. This case has been submitted — corrections now go through the
                            supervising therapist.
                        </div>
                    )}
                    {status.is_na && <div className="ch-note blue">Marked not applicable.</div>}

                    <SectionRenderer
                        section={section}
                        answers={answers}
                        onAnswer={onAnswer}
                        onBlur={() => flush(section.key)}
                        lang={lang}
                        disabled={!editable || status.is_na}
                        child={caseInfo}
                    />

                    <div className="ch-savebar">
                        <span>
                            {pendingDrafts > 0
                                ? `Saved on this device · ${pendingDrafts} waiting to sync`
                                : savedAt ? `Saved ${savedAt.toLocaleTimeString()} · ${user?.username}`
                                    : 'Saves automatically'}
                        </span>
                        <span className="ch-nav">
                            <button className="btn btn-secondary" onClick={() => goTo(prevKey(schema, section.key))}>← Previous</button>
                            <button className="btn btn-secondary" onClick={() => goTo(nextKey(schema, section.key))}>Next →</button>
                        </span>
                    </div>
                </section>
            </div>
        </div>
    );
}

function prevKey(schema, key) {
    const i = schema.sections.findIndex((s) => s.key === key);
    return schema.sections[Math.max(0, i - 1)].key;
}
function nextKey(schema, key) {
    const i = schema.sections.findIndex((s) => s.key === key);
    return schema.sections[Math.min(schema.sections.length - 1, i + 1)].key;
}
